import React, { useContext, useEffect, useState } from 'react';
import {
  Button, Form, Message, Segment, Tab, Header, Divider,
  Checkbox, Dropdown, Grid, GridColumn, Label,
  Accordion, Icon, Table, Confirm, Input,
} from 'semantic-ui-react';
import { createCognitoUser, addPartner, listUsers, manageUser } from '../libs/requests/adminRequests';
import { getPartners } from '../libs/requests/getPartnerDetails';
import { authContext } from '../containers/AuthProvider';

const TENANT_GROUP_MAP = {
  Nimans:    'NimansGroup',
  TechSmart: 'TechSmartUsers',
  Fibre1:    'Fibre1_Users',
  Hightide:  'HightideUsers',
  Firstcom:  'Firstcom_Users',
};

const GENDERS = [
  { key: 'Male',   value: 'Male',   text: 'Male' },
  { key: 'Female', value: 'Female', text: 'Female' },
  { key: 'Other',  value: 'Other',  text: 'Other / Prefer not to say' },
];

const DEFAULT_USER = {
  email: '',
  givenName: '',
  familyName: '',
  gender: 'Male',
  phoneNumber: '',
  password: '',
  tenant: '',
  customer: '',
  isReseller: false,
  isAdmin: false,
  orderAccess: true,
  servicesAccess: true,
  diagnosticsAccess: true,
  zenOnboarded: true,
  pendingOrders: false,
  groupName: '',
};

const FIXED_REALMS = ['bbn.net', 'dslnet'];

const DEFAULT_PARTNER = {
  tenant: '',
  partner: '',
  realms: FIXED_REALMS,
};

function buildSuppliersString(zenOnboarded) {
  return zenOnboarded ? '{Onboarded:{Zen:1}}' : '{Onboarded:{}}';
}

function FieldLabel({ label, description }) {
  return (
    <label>
      <strong>{label}</strong>
      {description && <span style={{ color: '#777', fontWeight: 'normal', marginLeft: 6, fontSize: '0.88em' }}>— {description}</span>}
    </label>
  );
}

function OnboardUserTab({ tenant }) {
  const groupName = TENANT_GROUP_MAP[tenant] || '';
  const defaultForm = { ...DEFAULT_USER, tenant, groupName };
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    getPartners().then(setPartners);
  }, []);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setSuccess('');
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.email || !form.givenName || !form.familyName || !form.password || !form.tenant || !form.customer) {
      setError('Email, First Name, Last Name, Password, Tenant and Customer are required.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: form.email.trim(),
        givenName: form.givenName.trim(),
        familyName: form.familyName.trim(),
        gender: form.gender,
        phoneNumber: form.phoneNumber.trim() ? '+44' + form.phoneNumber.trim().replace(/^0/, '') : '',
        password: form.password,
        tenant: form.tenant.trim(),
        customer: form.customer.trim(),
        isReseller: form.isReseller,
        isAdmin: form.isAdmin,
        orderAccess: form.orderAccess,
        servicesAccess: form.servicesAccess,
        diagnosticsAccess: form.diagnosticsAccess,
        suppliers: buildSuppliersString(form.zenOnboarded),
        pendingOrders: form.pendingOrders,
        groupName: form.groupName,
      };

      const result = await createCognitoUser(payload);
      if (result?.Success) {
        setSuccess(`User ${form.email} created successfully.`);
        setForm(defaultForm);
      } else {
        setError(typeof result === 'string' ? result : JSON.stringify(result));
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit} loading={loading}>
      <Header as="h3" dividing>Account Details</Header>

      <Form.Field required>
        <FieldLabel label="Email / Username" description="Used as the Cognito username and login credential" />
        <Form.Input
          placeholder="user@example.com"
          value={form.email}
          onChange={e => set('email', e.target.value)}
          type="email"
          autoComplete="off"
        />
      </Form.Field>

      <Grid columns={2} stackable>
        <GridColumn>
          <Form.Field required>
            <FieldLabel label="First Name" description="given_name attribute" />
            <Form.Input
              placeholder="John"
              value={form.givenName}
              onChange={e => set('givenName', e.target.value)}
              autoComplete="off"
            />
          </Form.Field>
        </GridColumn>
        <GridColumn>
          <Form.Field required>
            <FieldLabel label="Last Name" description="family_name attribute" />
            <Form.Input
              placeholder="Doe"
              value={form.familyName}
              onChange={e => set('familyName', e.target.value)}
              autoComplete="off"
            />
          </Form.Field>
        </GridColumn>
      </Grid>

      <Grid columns={2} stackable>
        <GridColumn>
          <Form.Field>
            <FieldLabel label="Gender" />
            <Dropdown
              selection
              options={GENDERS}
              value={form.gender}
              onChange={(_, d) => set('gender', d.value)}
              fluid
            />
          </Form.Field>
        </GridColumn>
        <GridColumn>
          <Form.Field>
            <FieldLabel label="Phone Number" />
            <Form.Input
              label="+44"
              placeholder="7911 123456"
              value={form.phoneNumber}
              onChange={e => set('phoneNumber', e.target.value)}
              autoComplete="off"
            />
          </Form.Field>
        </GridColumn>
      </Grid>

      <Form.Field required>
        <FieldLabel label="Password" description="Set as the user's permanent password — no temporary-password flow" />
        <Form.Input
          placeholder="Min 8 chars, upper, lower, number, symbol"
          value={form.password}
          onChange={e => set('password', e.target.value)}
          type="password"
          autoComplete="new-password"
        />
      </Form.Field>

      <Divider />
      <Header as="h3" dividing>Tenant &amp; Partner</Header>

      <Grid columns={2} stackable>
        <GridColumn>
          <Form.Field>
            <FieldLabel label="Tenant (custom:tennant)" />
            <Form.Input value={form.tenant} readOnly />
          </Form.Field>
        </GridColumn>
        <GridColumn>
          <Form.Field required>
            <FieldLabel label="Customer (custom:customer)" description="The partner/company name this user belongs to" />
            <Dropdown
              selection
              search
              fluid
              options={partners.map(p => ({ key: p, value: p, text: p }))}
              value={form.customer}
              onChange={(_, d) => set('customer', d.value)}
              placeholder="Select a partner..."
              noResultsMessage="No partners found"
              data-testid="customer-dropdown"
            />
          </Form.Field>
        </GridColumn>
      </Grid>

      <Form.Field>
        <FieldLabel label="Cognito Group" />
        <Form.Input value={form.groupName} readOnly />
      </Form.Field>

      <Divider />
      <Header as="h3" dividing>Permissions</Header>

      <Message info style={{ marginBottom: 12 }}>
        <Grid columns={2} stackable divided>
          <GridColumn>
            <strong>Nimans Admin</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: 18, fontSize: '0.9em' }}>
              <li>Is Admin ✓ &nbsp;|&nbsp; Is Reseller ✗</li>
              <li>Order, Services, Diagnostics Access ✓</li>
              <li>Pending Orders ✓</li>
            </ul>
            <Button
              type="button"
              size="mini"
              color="blue"
              style={{ marginTop: 8 }}
              onClick={() => {
                set('isAdmin', true);
                set('isReseller', false);
                set('pendingOrders', true);
                set('orderAccess', true);
                set('servicesAccess', true);
                set('diagnosticsAccess', true);
              }}
            >
              Apply Admin Defaults
            </Button>
          </GridColumn>
          <GridColumn>
            <strong>Reseller</strong>
            <ul style={{ margin: '6px 0 0 0', paddingLeft: 18, fontSize: '0.9em' }}>
              <li>Is Reseller ✓ &nbsp;|&nbsp; Is Admin ✗</li>
              <li>Order, Services, Diagnostics Access ✓</li>
              <li>Pending Orders ✗</li>
            </ul>
            <Button
              type="button"
              size="mini"
              color="teal"
              style={{ marginTop: 8 }}
              onClick={() => {
                set('isReseller', true);
                set('isAdmin', false);
                set('pendingOrders', false);
                set('orderAccess', true);
                set('servicesAccess', true);
                set('diagnosticsAccess', true);
              }}
            >
              Apply Reseller Defaults
            </Button>
          </GridColumn>
        </Grid>
      </Message>

      <Grid columns={2} stackable>
        <GridColumn>
          <Form.Field>
            <Checkbox
              label={<label><strong>Is Reseller</strong> — Reseller partner (do not combine with Is Admin)</label>}
              checked={form.isReseller}
              onChange={(_, d) => set('isReseller', d.checked)}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              label={<label><strong>Is Admin</strong> — Nimans admin (do not combine with Is Reseller)</label>}
              checked={form.isAdmin}
              onChange={(_, d) => set('isAdmin', d.checked)}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              label={<label><strong>Order Access</strong> — Can place and view orders</label>}
              checked={form.orderAccess}
              onChange={(_, d) => set('orderAccess', d.checked)}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              label={<label><strong>Services Access</strong> — Can view active services and faults</label>}
              checked={form.servicesAccess}
              onChange={(_, d) => set('servicesAccess', d.checked)}
            />
          </Form.Field>
        </GridColumn>
        <GridColumn>
          <Form.Field>
            <Checkbox
              label={<label><strong>Diagnostics Access</strong> — Can run line diagnostics</label>}
              checked={form.diagnosticsAccess}
              onChange={(_, d) => set('diagnosticsAccess', d.checked)}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              label={<label><strong>Pending Orders</strong> — Nimans admins only; not for resellers</label>}
              checked={form.pendingOrders}
              onChange={(_, d) => set('pendingOrders', d.checked)}
            />
          </Form.Field>
        </GridColumn>
      </Grid>

      <Divider />
      <Header as="h3" dividing>Supplier Onboarding (custom:suppliers)</Header>
      <p style={{ color: '#555', fontSize: '0.9em' }}>
        Controls which wholesale networks the user can order from. Builds the <code>{`{Onboarded:{Zen:1}}`}</code> attribute.
      </p>

      <Form.Field>
        <Checkbox
          label={<label><strong>Zen (BT Wholesale)</strong> — FTTC, FTTP, copper, G.Fast products</label>}
          checked={form.zenOnboarded}
          onChange={(_, d) => set('zenOnboarded', d.checked)}
        />
      </Form.Field>

      <div style={{ marginTop: 8, marginBottom: 16 }}>
        <Label pointing="above">
          Will be stored as: <code>{buildSuppliersString(form.zenOnboarded)}</code>
        </Label>
      </div>

      {error && <Message negative content={error} />}
      {success && <Message positive content={success} />}

      <Button primary type="submit" loading={loading} disabled={loading}>
        Create User
      </Button>
    </Form>
  );
}

function AddPartnerTab({ tenant }) {
  const defaultForm = { ...DEFAULT_PARTNER, tenant };
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setSuccess('');
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.tenant || !form.partner) {
      setError('Tenant and Partner are required.');
      return;
    }

    setLoading(true);
    try {
      const result = await addPartner({
        tenant: form.tenant.trim(),
        partner: form.partner.trim(),
        realms: form.realms,
      });

      if (result?.Success) {
        setSuccess(`Partner '${form.partner}' added for tenant '${form.tenant}'.`);
        setForm(defaultForm);
      } else {
        setError(typeof result === 'string' ? result : JSON.stringify(result));
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form onSubmit={handleSubmit} loading={loading}>
      <Message info>
        <Message.Header>Partners cannot be overwritten</Message.Header>
        <p>If a partner already exists for the given tenant, the request will be rejected. Use this form to add new partners only.</p>
      </Message>

      <Header as="h3" dividing>Partner Details</Header>

      <Grid columns={2} stackable>
        <GridColumn>
          <Form.Field>
            <FieldLabel label="Tenant" />
            <Form.Input value={form.tenant} readOnly />
          </Form.Field>
        </GridColumn>
        <GridColumn>
          <Form.Field required>
            <FieldLabel
              label="Partner"
              description="The range key — partner/company name, e.g. ACME Communications"
            />
            <Form.Input
              placeholder="ACME Communications"
              value={form.partner}
              onChange={e => set('partner', e.target.value)}
            />
          </Form.Field>
        </GridColumn>
      </Grid>

      <Form.Field>
        <FieldLabel label="Realms" />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {FIXED_REALMS.map(r => (
            <Label key={r}>{r}</Label>
          ))}
        </div>
      </Form.Field>

      {error && <Message negative content={error} />}
      {success && <Message positive content={success} />}

      <Button primary type="submit" loading={loading} disabled={loading}>
        Add Partner
      </Button>
    </Form>
  );
}

const STATUS_COLOURS = {
  CONFIRMED: 'green',
  FORCE_CHANGE_PASSWORD: 'orange',
  UNCONFIRMED: 'yellow',
  RESET_REQUIRED: 'orange',
};

function UserStatusBadge({ status, enabled }) {
  if (!enabled) return <Label size="tiny" color="grey">Disabled</Label>;
  const colour = STATUS_COLOURS[status] || 'grey';
  const text = status === 'FORCE_CHANGE_PASSWORD' ? 'Reset Required'
             : status === 'CONFIRMED'              ? 'Active'
             : status;
  return <Label size="tiny" color={colour}>{text}</Label>;
}

function ManageUsersTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState('');
  const [search, setSearch] = useState('');

  const filteredGroups = search.trim()
    ? groups.filter(g =>
        g.Partner.toLowerCase().includes(search.toLowerCase()) ||
        g.Users.some(u => u.Email.toLowerCase().includes(search.toLowerCase()))
      )
    : groups;

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const result = await listUsers();
      if (Array.isArray(result)) {
        setGroups(result);
      } else {
        setError(typeof result === 'string' ? result : 'Failed to load users.');
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  }

  function requestConfirm(action, email) {
    const messages = {
      reset_password: `Send a password reset to ${email}? They will receive a reset code by email.`,
      deactivate:     `Deactivate ${email}? They will not be able to log in until reactivated.`,
      reactivate:     `Reactivate ${email}? They will be able to log in again.`,
      delete:         `Permanently delete ${email}? This cannot be undone.`,
    };
    setConfirm({ action, email, message: messages[action] });
  }

  async function executeAction() {
    const { action, email } = confirm;
    setConfirm(null);
    setActionLoading(email);
    setError('');
    setSuccessMsg('');
    try {
      const result = await manageUser(action, email);
      if (result?.Success) {
        setSuccessMsg(result.Message || 'Done.');
        await load();
      } else {
        setError(typeof result === 'string' ? result : JSON.stringify(result));
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred.');
    } finally {
      setActionLoading('');
    }
  }

  const confirmButtonLabel = {
    reset_password: 'Send Reset',
    deactivate:     'Yes, Deactivate',
    reactivate:     'Yes, Reactivate',
    delete:         'Yes, Delete',
  };

  const confirmButtonColour = {
    reset_password: 'blue',
    deactivate:     'orange',
    reactivate:     'green',
    delete:         'red',
  };

  const panels = filteredGroups.map((group, i) => ({
    key: group.Partner,
    title: {
      content: (
        <span style={{ fontSize: '1em' }}>
          <strong>{group.Partner}</strong>
          <span style={{ color: '#888', marginLeft: 10, fontSize: '0.88em', fontWeight: 'normal' }}>
            {group.Users.length} user{group.Users.length !== 1 ? 's' : ''}
          </span>
        </span>
      ),
    },
    content: {
      content: (
        <Table basic="very" compact size="small" style={{ marginTop: 0 }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {group.Users.map(user => {
              const busy = actionLoading === user.Email;
              return (
                <Table.Row key={user.Email}>
                  <Table.Cell>{user.Email}</Table.Cell>
                  <Table.Cell>
                    <UserStatusBadge status={user.Status} enabled={user.Enabled} />
                  </Table.Cell>
                  <Table.Cell>
                    <Button.Group size="mini">
                      <Button
                        basic color="blue"
                        loading={busy} disabled={busy}
                        onClick={() => requestConfirm('reset_password', user.Email)}
                      >
                        Reset Password
                      </Button>
                      {user.Enabled ? (
                        <Button
                          basic color="orange"
                          loading={busy} disabled={busy}
                          onClick={() => requestConfirm('deactivate', user.Email)}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          basic color="green"
                          loading={busy} disabled={busy}
                          onClick={() => requestConfirm('reactivate', user.Email)}
                        >
                          Reactivate
                        </Button>
                      )}
                      <Button
                        basic color="red"
                        loading={busy} disabled={busy}
                        onClick={() => requestConfirm('delete', user.Email)}
                      >
                        Delete
                      </Button>
                    </Button.Group>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      ),
    },
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Header as="h3" style={{ margin: 0 }}>Partner Users</Header>
        <Button
          size="small"
          onClick={load}
          loading={loading}
          disabled={loading}
        >
          <Icon name="refresh" />
          Refresh
        </Button>
      </div>

      <Input
        icon="search"
        placeholder="Filter by partner or email…"
        value={search}
        onChange={(_, { value }) => setSearch(value)}
        style={{ marginBottom: 16, width: '100%' }}
      />

      {error && (
        <Message negative onDismiss={() => setError('')} content={error} />
      )}
      {successMsg && (
        <Message positive onDismiss={() => setSuccessMsg('')} content={successMsg} />
      )}

      {loading && groups.length === 0 ? (
        <Message info content="Loading users…" />
      ) : filteredGroups.length === 0 ? (
        <Message info content={search.trim() ? 'No partners or users match your search.' : 'No users found.'} />
      ) : (
        <Accordion
          fluid
          styled
          panels={panels}
          activeIndex={activeIndex}
          onTitleClick={(_, { index }) =>
            setActiveIndex(activeIndex === index ? -1 : index)
          }
        />
      )}

      <Confirm
        open={!!confirm}
        header={
          confirm?.action === 'delete'     ? 'Delete User'
        : confirm?.action === 'deactivate' ? 'Deactivate User'
        : confirm?.action === 'reactivate' ? 'Reactivate User'
        : 'Reset Password'
        }
        content={confirm?.message}
        confirmButton={
          <Button
            color={confirm ? confirmButtonColour[confirm.action] : undefined}
            content={confirm ? confirmButtonLabel[confirm.action] : 'Confirm'}
          />
        }
        onCancel={() => setConfirm(null)}
        onConfirm={executeAction}
      />
    </div>
  );
}

export default function AdminOnboarding() {
  const { permissions } = useContext(authContext);
  const tenant = permissions?.tenant || '';

  const panes = [
    {
      menuItem: 'Onboard User',
      render: () => (
        <Tab.Pane>
          <OnboardUserTab tenant={tenant} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Add Partner',
      render: () => (
        <Tab.Pane>
          <AddPartnerTab tenant={tenant} />
        </Tab.Pane>
      ),
    },
    {
      menuItem: 'Manage Users',
      render: () => (
        <Tab.Pane>
          <ManageUsersTab />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      <Header as="h2">Admin — Onboarding</Header>
      <p style={{ color: '#555' }}>
        Use this page to onboard new portal users (Cognito), add new partners to the system (DynamoDB),
        or manage existing users — reset passwords, deactivate, or delete accounts.
        This page is visible to admin users only.
      </p>
      <Segment>
        <Tab panes={panes} />
      </Segment>
    </div>
  );
}
